import { Badge, Button, Divider, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { dataEHoraPorExtenso, dataSimples, moeda } from '@/comuns/formato';
import { conteudoEmConsumo, custoDeConsumoCentavos, quantidade, unidadeAbrev, unidadeLabel } from '@/comuns/unidades';
import type { MovimentacaoDeEstoque, Produto, SituacaoDoProduto } from '@/data/tipos';

export const SITUACAO: Record<SituacaoDoProduto, { rotulo: string; tom: 'success' | 'warning' | 'danger' }> = {
    normal: { rotulo: 'Normal', tom: 'success' },
    baixo: { rotulo: 'Baixo', tom: 'warning' },
    critico: { rotulo: 'Crítico', tom: 'danger' },
};

export interface ProdutoDetalheProps {
    produto: Produto | null;
    movimentacoes: MovimentacaoDeEstoque[];
    onClose: () => void;
    onEditar: (produto: Produto) => void;
    onMovimentar: (produto: Produto, tipo: 'entrada' | 'saida') => void;
    /** Leva à sugestão de compra. Avisar que acabou sem dar o caminho é meio aviso. */
    onRepor: (produto: Produto) => void;
}

/**
 * Ficha do produto.
 *
 * Entrada e saída ficam aqui porque é onde a pessoa já está olhando o saldo:
 * mandá-la de volta à lista para a ação mais frequente do estoque é o caminho
 * longo para o caso comum.
 */
export function ProdutoDetalhe({ produto, movimentacoes, onClose, onEditar, onMovimentar, onRepor }: ProdutoDetalheProps) {
    if (!produto) return null;

    const situacao = SITUACAO[produto.situacao];
    const doProduto = movimentacoes.filter((m) => m.produtoId === produto.id).slice(0, 5);

    return (
        <Modal
            open onClose={onClose} width={560}
            title={produto.nome}
            description={[produto.marca, produto.fornecedor, produto.categoria].filter(Boolean).join(' · ')}
            footer={
                <>
                    <Button variant="secondary" iconLeft={<Icon name="pencil" size={15} />} onClick={() => onEditar(produto)}>Editar</Button>
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="secondary" iconLeft={<Icon name="arrow-down-to-line" size={15} />} onClick={() => onMovimentar(produto, 'entrada')}>Entrada</Button>
                    <Button iconLeft={<Icon name="arrow-up-from-line" size={15} />} onClick={() => onMovimentar(produto, 'saida')}>Saída</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                    <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-secondary)' }}>Disponível</span>
                        <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                            {quantidade(produto.disponivel)} {unidadeLabel(produto.disponivel, produto.unidade)}
                        </span>
                    </span>
                    <span style={{ marginLeft: 'auto', textAlign: 'right' }}>
                        <Badge tone={situacao.tom} dot>{situacao.rotulo}</Badge>
                        <span style={{ display: 'block', marginTop: 5, fontSize: 12, color: 'var(--text-tertiary)' }}>
                            mínimo {quantidade(produto.minimo)} {unidadeAbrev(produto.unidade)}
                        </span>
                    </span>
                </div>

                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 20px', fontSize: 13 }}>
                    <dt style={{ color: 'var(--text-secondary)' }}>Em estoque</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>
                        {quantidade(produto.quantidade)} {unidadeLabel(produto.quantidade, produto.unidade)}
                        {produto.reservado > 0 && ` · ${quantidade(produto.reservado)} reservados por propostas`}
                    </dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Unidade de consumo</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>
                        {unidadeAbrev(produto.unidadeConsumo)} · 1 {unidadeAbrev(produto.unidade)} de {quantidade(produto.conteudo)}{' '}
                        {unidadeAbrev(produto.unidadeConteudo)} = {quantidade(conteudoEmConsumo(produto))} {unidadeAbrev(produto.unidadeConsumo)}
                    </dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Custo</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                        {moeda(produto.custoCentavos)}/{unidadeAbrev(produto.unidade)} · {moeda(custoDeConsumoCentavos(produto))}/{unidadeAbrev(produto.unidadeConsumo)}
                    </dd>
                    <dt style={{ color: 'var(--text-secondary)' }}>Lote e validade</dt>
                    <dd style={{ margin: 0, textAlign: 'right', fontWeight: 500 }}>
                        {produto.lote || '—'} · {produto.validade ? dataSimples(produto.validade) : 'sem validade'}
                    </dd>
                </dl>

                {produto.situacao !== 'normal' && (
                    <Alert
                        tone={produto.situacao === 'critico' ? 'danger' : 'warning'}
                        title={`Estoque ${situacao.rotulo.toLowerCase()}`}
                        dismissible={false}
                        action={(
                            <Button size="sm" variant="secondary" onClick={() => onRepor(produto)}>Criar pedido de compra</Button>
                        )}
                    >
                        {quantidade(produto.disponivel)} {unidadeLabel(produto.disponivel, produto.unidade)} contra mínimo de {quantidade(produto.minimo)}.
                    </Alert>
                )}

                <Divider label="Últimas movimentações" spacing={2} />
                {doProduto.length === 0 ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhuma movimentação registrada.</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {doProduto.map((m) => (
                            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13 }}>
                                <span style={{ width: 116, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{dataEHoraPorExtenso(m.ocorridoEm)}</span>
                                <Badge tone={m.tipo === 'entrada' ? 'success' : 'neutral'}>{m.tipo === 'entrada' ? 'Entrada' : 'Saída'}</Badge>
                                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-secondary)' }}>{m.origem}</span>
                                {/* O que saiu vai na unidade em que foi usado: "0,5
                                    frasco" não conta a história que "50 UI" conta. */}
                                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {m.tipo === 'entrada' ? '+' : '−'}
                                    {m.quantidadeConsumo != null
                                        ? `${quantidade(m.quantidadeConsumo)} ${unidadeAbrev(m.unidadeConsumo)}`
                                        : `${quantidade(m.quantidade)} ${unidadeAbrev(m.unidade)}`}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
}
